// frontend/src/utils/backgroundRun.js
//
// How a merchant-started background run READS.
//
// A long write (the cost import, creating suppliers from vendors) runs as a
// background pipeline run: the POST answers 202 with a run that has only just
// been QUEUED and carries no result, and every number the merchant sees comes
// from polling the run's status endpoint
// (backend/core/scheduling/queue.py pipeline_run_state). Turning that pair
// into "what belongs on screen" takes four judgements:
//
//   - a poll answer only counts when it is about the run WE started. Without
//     that guard, a retry renders the PREVIOUS run's cached result for as
//     long as the first poll of the new one is in flight — a window that
//     flashes "complete" over a run that has just started.
//   - "finished" is `!in_progress`, never "has a summary". Deriving it from the
//     summary spins forever on a finished run that produced none, which is
//     exactly the case the merchant most needs told about.
//   - a finished run with no summary is a FAILURE, not a zero-item success.
//     Blank counters read as "nothing to do"; a merchant who believes that
//     acts on a change that was never written.
//   - a poll that FAILS is not a run that is still going. `statusUnavailable`
//     is that state, and it is NOT `failed` — the run itself may be fine; what
//     broke is our ability to report on it.

/**
 * What the page should render for one background run.
 *
 * @param {object} args
 * @param {{id: number, outcome: string}|null} args.run
 *   The run this page started, as returned by POST — `null` before the POST
 *   resolves and while a retry is in flight.
 * @param {object|null} args.polled
 *   The latest status payload, or null before the first poll answers.
 * @param {boolean} [args.startFailed]
 *   The POST itself was rejected. Nothing was queued, so there is no run to
 *   report progress for and the caller renders its own start-error copy.
 * @param {boolean} [args.pollFailed]
 *   The status query errored. The run may well still be running — this says
 *   only that we cannot see it.
 * @returns {{
 *   pending: boolean,           // queued, running, or awaiting the first poll
 *   queued: boolean,            // pending AND not started yet
 *   queueDelayed: boolean,      // the row exists but the queue would not take it
 *   statusUnavailable: boolean, // the poll failed and the run is unresolved
 *   succeeded: boolean,
 *   failed: boolean,
 *   summary: object|null,       // the run's own result; only when succeeded
 *   errorCode: string|null,     // backend failure code, '' when not merchant-fixable
 * }}
 */
export function backgroundRunView({ run, polled, startFailed = false, pollFailed = false }) {
  const state = polled && run && polled.run_id === run.id ? polled : null;

  const finished = !!state && !state.in_progress;
  const summary = finished && state.status === 'succeeded' ? state.summary : null;
  const succeeded = !!summary;
  const failed = finished && !succeeded;
  // A poll failure only matters while the run is unresolved AND there is a run
  // at all. Two exclusions, both of which put a second banner on screen if you
  // leave them out:
  //   - once a finished run's result is in hand, a later failing tick must
  //     not take it off the screen (polling has stopped by then anyway);
  //   - a failed START leaves the status query disabled but its LAST error
  //     still readable, and "we can't see your run" beside "we couldn't
  //     start one" describes a run that does not exist.
  const statusUnavailable = pollFailed && !finished && !startFailed;
  const pending = !startFailed && !statusUnavailable && !finished;

  return {
    pending,
    queued: pending && (!state || state.status === 'pending'),
    queueDelayed: pending && run?.outcome === 'queue_unavailable',
    statusUnavailable,
    succeeded,
    failed,
    summary,
    errorCode: state?.error_code ?? null,
  };
}
